import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import ApiCrudSection, {
  type Column,
  type FormField,
  type Row,
} from "../components/ApiCrudSection";
import {
  fetchAdjustments,
  createAdjustment,
  updateAdjustment,
  deleteAdjustment,
} from "../services/payrollService";
import {
  ADJUSTMENT_TYPE_OPTIONS,
  adjustmentTypeLabel,
  ADJUSTMENT_TYPE_TONES,
  adjustmentStatusLabel,
  ADJUSTMENT_STATUS_TONES,
  buildThemeClasses,
  formatMoney,
} from "../theme";
import type { PayrollAdjustment, AdjustmentType, AdjustmentNoteEntry } from "../types";
import { useIsStaffAdmin } from "../../../hooks/useIsStaffAdmin";
import ExtraordinaryMovements from "../components/ExtraordinaryMovements";
import SalaryHistoryPanel, { type SalaryDraft } from "../components/SalaryHistoryPanel";

// ---------------------------------------------------------------------------
// Anticipos y otros haberes/descuentos (AgroCloud#37).
//
// Todo lo que suma o resta en la liquidación sin ser trabajo: anticipos,
// préstamos en cuotas, descuentos y haberes/bonos.
//
// El SIGNO no se elige: lo da el tipo (`ADJUSTMENT_SIGN` en `@torre/payroll`).
// Un anticipo siempre resta y un bono siempre suma; que el usuario pudiera
// invertirlo con un signo a mano sería una fuente de sueldos mal pagados.
//
// El SALDO de un préstamo lo recalcula el backend en cada listado: es un dato
// derivado, no un número que se pueda quedar obsoleto en Firestore.
//
// Un movimiento ya `applied` a una liquidación no se edita ni se borra (el
// backend responde 409): alteraría un sueldo ya pagado sin dejar rastro. Se
// corrige registrando un ajuste nuevo.
// ---------------------------------------------------------------------------

function toPayload(values: Record<string, string>): Partial<PayrollAdjustment> {
  const installmentsRaw = String(values.installments ?? "").trim();
  return {
    date: values.date,
    teamMemberId: values.teamMemberId,
    teamMemberName: values.teamMemberName || null,
    type: (values.type || "advance") as AdjustmentType,
    concept: values.concept,
    amount: Number(values.amount) || 0,
    installments: installmentsRaw === "" ? null : Number(installmentsRaw),
    taxable: values.taxable !== "false",
    periodId: values.periodId || null,
    notes: values.notes || null,
  };
}

export default function AdjustmentsSection() {
  // #1007: solo quien administra modifica; los demás ven lo suyo en solo lectura.
  const isStaffAdmin = useIsStaffAdmin("payroll");
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [rows, setRows] = useState<PayrollAdjustment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // #1487: un renglón de movimientos puede proponer un aumento al historial de sueldos.
  const [raiseDraft, setRaiseDraft] = useState<SalaryDraft | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items } = await fetchAdjustments();
      setRows(items);
    } catch {
      setError(t("payroll.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Deuda viva del personal: anticipos + saldos de préstamo pendientes (AgroCloud#37). */
  const totalPending = useMemo(
    () =>
      rows
        .filter((m) => (m.status ?? "pending") === "pending")
        .filter((m) => m.type === "advance" || m.type === "loan")
        .reduce((acc, m) => {
          const balance = (m as PayrollAdjustment & { pendingBalance?: number }).pendingBalance;
          return acc + (balance ?? Number(m.amount) ?? 0);
        }, 0),
    [rows],
  );

  const badge = (tone: string, label: string) => (
    <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${tone}`}>{label}</span>
  );

  const columns: Column[] = [
    { key: "date", label: t("payroll.fields.date"), width: "104px" },
    {
      key: "teamMemberName",
      label: t("payroll.fields.teamMember"),
      render: (row) => String(row.teamMemberName || row.teamMemberId || "—"),
    },
    {
      key: "type",
      label: t("payroll.fields.adjustmentType"),
      render: (row) => {
        const type = String(row.type || "advance");
        return badge(
          ADJUSTMENT_TYPE_TONES[type] ?? "bg-gray-400/15 text-gray-500",
          adjustmentTypeLabel(t, type),
        );
      },
    },
    { key: "concept", label: t("payroll.fields.concept") },
    {
      key: "amount",
      label: t("payroll.fields.amount"),
      align: "right",
      // El signo lo dicta el tipo, no un campo editable.
      render: (row) => {
        const type = String(row.type || "advance");
        const remaining = type === "advance" || type === "loan" || type === "deduction";
        const amount = formatMoney(row.amount as number, String(row.currency || "MXN"));
        return (
          <span className={remaining ? "text-red-500" : "text-emerald-500"}>
            {remaining ? "−" : "+"} {amount}
          </span>
        );
      },
    },
    {
      key: "installments",
      label: t("payroll.fields.installments"),
      align: "center",
      render: (row) => {
        const installments = row.installments as number | null | undefined;
        if (!installments || installments <= 1) return "—";
        const paid = Number(row.paidInstallments) || 0;
        return `${paid}/${installments}`;
      },
    },
    {
      key: "pendingBalance",
      label: t("payroll.fields.pendingBalance"),
      align: "right",
      render: (row) => {
        const balance = row.pendingBalance as number | undefined;
        if (balance === undefined) return "—";
        return formatMoney(balance, String(row.currency || "MXN"));
      },
    },
    {
      key: "status",
      label: t("payroll.fields.adjustmentStatus"),
      render: (row) => {
        const status = String(row.status || "pending");
        return badge(
          ADJUSTMENT_STATUS_TONES[status] ?? "bg-gray-400/15 text-gray-500",
          adjustmentStatusLabel(t, status),
        );
      },
    },
    {
      // #1487: la nota vigente y, si hubo otras, su historial (quién y cuándo).
      key: "notes",
      label: t("payroll.fields.notes"),
      render: (row) => {
        const notes = String(row.notes || "");
        const history = (row.notesHistory as AdjustmentNoteEntry[] | undefined) ?? [];
        const showHistory = history.length > 1 || (history.length === 1 && history[0].note !== notes);
        return (
          <div className="max-w-[260px]">
            <span className="block truncate" title={notes}>
              {notes || "—"}
            </span>
            {showHistory && (
              <details className="text-xs">
                <summary className={`cursor-pointer ${tc.textSecondary}`}>
                  {t("payroll.fields.notesHistory")} ({history.length})
                </summary>
                <ul className="mt-1 space-y-1">
                  {[...history].reverse().map((h, i) => (
                    <li key={`${h.at}-${i}`}>
                      <span className={tc.text}>{h.note}</span>
                      <span className={`block ${tc.textSecondary}`}>
                        {h.byName || h.by || "—"} · {String(h.at).slice(0, 10)}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        );
      },
    },
  ];

  const fields: FormField[] = [
    { key: "date", label: t("payroll.fields.date"), type: "date", required: true },
    { key: "teamMemberId", label: t("payroll.fields.teamMemberId"), required: true },
    { key: "teamMemberName", label: t("payroll.fields.teamMemberName") },
    {
      key: "type",
      label: t("payroll.fields.adjustmentType"),
      type: "select",
      defaultValue: "advance",
      hint: t("payroll.hints.adjustmentSign"),
      options: ADJUSTMENT_TYPE_OPTIONS.map((v) => ({
        value: v,
        label: adjustmentTypeLabel(t, v),
      })),
    },
    {
      key: "concept",
      label: t("payroll.fields.concept"),
      required: true,
      hint: t("payroll.hints.concept"),
    },
    { key: "amount", label: t("payroll.fields.amount"), type: "number", required: true },
    {
      key: "installments",
      label: t("payroll.fields.installments"),
      type: "number",
      hint: t("payroll.hints.installments"),
    },
    {
      key: "taxable",
      label: t("payroll.fields.taxable"),
      type: "checkbox",
      defaultValue: true,
      hint: t("payroll.hints.taxable"),
    },
    { key: "periodId", label: t("payroll.fields.periodId"), hint: t("payroll.hints.adjustmentPeriod") },
    { key: "notes", label: t("payroll.fields.notes"), type: "textarea" },
  ];

  const banner = (
    <div className="mb-4">
      {/* #1376: lo que nadie capturó pero sí se pagó, detectado en pagos y gastos de nómina. */}
      <ExtraordinaryMovements
        canReview={isStaffAdmin}
        onRegisterRaise={(row) =>
          setRaiseDraft({
            teamMemberId: row.teamMemberId,
            teamMemberName: row.teamMemberName,
            effectiveFrom: row.date,
            amount: row.amount,
            expected: row.expected,
            difference: row.difference,
            currency: row.currency,
          })
        }
      />
      {/* #1487: cuánto ganaba cada quien y desde cuándo; explica los «monto distinto» tras un aumento. */}
      <SalaryHistoryPanel canEdit={isStaffAdmin} draft={raiseDraft} onDraftConsumed={() => setRaiseDraft(null)} />
      <div className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
        <p className={`text-xs ${tc.textSecondary}`}>{t("payroll.totalPending")}</p>
        <p className={`text-xl font-bold ${tc.text}`}>{formatMoney(totalPending)}</p>
        <p className={`mt-1 text-xs ${tc.textSecondary}`}>{t("payroll.totalPendingHint")}</p>
      </div>
    </div>
  );

  const handleCreate = async (values: Record<string, string>) => {
    await createAdjustment(toPayload(values));
    await load();
  };

  const handleUpdate = async (id: string, values: Record<string, string>) => {
    await updateAdjustment(id, toPayload(values));
    await load();
  };

  const handleDelete = async (id: string) => {
    await deleteAdjustment(id);
    await load();
  };

  return (
    <ApiCrudSection
      readOnly={!isStaffAdmin}
      title={t("payroll.menu.adjustments")}
      subtitle={t("payroll.adjustmentsSubtitle")}
      icon="ri-hand-coin-line"
      addLabel={t("payroll.newAdjustment")}
      columns={columns}
      fields={fields}
      rows={rows as unknown as Row[]}
      loading={loading}
      error={error}
      searchKey="teamMemberName"
      banner={banner}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
    />
  );
}
